'use client';
import { projectName } from '../../../packages/plugin-shared/projectChoice.mjs';
import { useMemo, useRef, useState } from 'react';
import { Clock, LocateFixed, Puzzle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal, ModalBody, ModalFooter } from '../../components/ui/Modal';
import { type ManageSelectionItem } from '../../components/ui/ManageSelectionModal';
import { ManagedSelectionField, RowPicker, type RowPickerItem } from '../../components/ui/RowPicker';
import { brainModelSelection } from '../../components/ui/brainModelSelection';
import { ModelIcon } from '../../components/ui/ModelIcon';
import { ModelSelection } from '../../components/ui/ModelSelection';
import { ProjectIcon } from '../../components/ui/ProjectIcon';
import { ChoiceField } from '../../components/ui/ChoiceField';
import { normalizeTokenList, TokenList } from '../../components/ui/TokenList';
import { DirectoryPicker } from '../../components/ui/DirectoryPicker';
import { Avatar } from '../../components/ui/Avatar';
import { ToolMark } from '../../components/ui/ToolMark';
import { useTranslation } from '../../lib/i18n';
import { useConfig, useBrainOauthStatus, useNotificationDestinations, usePlugins, usePluginTools, useProjects, useUsers } from '../../lib/queries';
import type { BrainModelOption } from '../../lib/types';
import { connectionLogin } from '../../lib/providerEdits';

/** Generic `destination` config field: the shared single-choice dropdown over the live notification
 *  catalog. A saved value the catalog no longer lists stays pinned and selected. */
export function DestinationField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const { data: destinations = [] } = useNotificationDestinations();
  const selected = destinations.find((destination) => destination.value === value);
  const items: ManageSelectionItem[] = [
    { id: '', label: t.managePicker.none, group: '' },
    ...(value && !selected ? [{ id: value, label: value, group: '' }] : []),
    ...destinations.map((destination) => ({
      id: destination.value,
      label: destination.label,
      group: `${destination.platform}:${destination.group ?? destination.platform}`,
      groupLabel: destination.group ?? destination.platform,
      badges: destination.subtitle ? [{ text: destination.subtitle, tone: 'muted' as const }] : undefined,
    })),
  ];
  return (
    <RowPicker
      label={label}
      summary={selected?.label ?? (value || t.managePicker.none)}
      items={items}
      value={value}
      onChange={onChange}
    />
  );
}

/** Generic `user` config field: one account from the admin user directory, shown with its avatar and
 *  stored as its username. A saved username the directory no longer lists stays pinned and marked. */
export function UserField({ label, value, required, onChange }: { label: string; value: string; required?: boolean; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const { data: users = [], isPending, isError } = useUsers();
  const selected = users.find((user) => user.username === value);
  const items: RowPickerItem[] = [
    ...(required ? [] : [{ id: '', label: t.managePicker.none, group: '' }]),
    ...(value && !selected && !isPending ? [{ id: value, label: value, group: '', status: { kind: 'error' as const, label: t.pluginCfg.userMissing, tone: 'muted' as const } }] : []),
    ...users.map((user) => ({
      id: user.username,
      label: user.name.trim() || user.username,
      description: user.name.trim() ? user.username : undefined,
      group: '',
      icon: <Avatar user={user} size={24} />,
    })),
  ];
  return (
    <RowPicker
      label={label}
      summary={selected ? (selected.name.trim() || selected.username) : (value || t.managePicker.none)}
      icon={selected ? <Avatar user={selected} size={20} /> : undefined}
      items={items}
      value={value}
      onChange={onChange}
      notice={isPending ? t.common.loading : isError ? t.pluginCfg.usersLoadFailed : undefined}
    />
  );
}

/** Generic `multiSelect` config field over the manifest's own options (one ungrouped list, so no
 *  group-filter row). Saved values the manifest no longer offers stay selectable. */
export function MultiSelectField({ label, options, value, onChange }: { label: string; options: { value: string; label: string }[]; value: string[]; onChange: (v: string[]) => void }) {
  return <CatalogMultiSelectField label={label} items={options.map((o) => ({ id: o.value, label: o.label, group: '' }))} value={value} onChange={onChange} />;
}

const supportedTimezones = (): string[] => {
  try {
    const supportedValuesOf = (Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] }).supportedValuesOf;
    return supportedValuesOf?.call(Intl, 'timeZone') ?? [];
  } catch { return []; }
};
const browserTimezone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
  catch { return ''; }
};

const CUSTOM_TIMEZONE = '__custom_timezone__';

export function TimezoneField({ label, hint, value, onChange }: { label: string; hint?: string; value: string; onChange: (value: string | null) => void }) {
  const { t } = useTranslation();
  const [customOpen, setCustomOpen] = useState(false);
  // "Custom…" was chosen: the custom dialog opens once the dropdown has closed and handed focus back to
  // the trigger (`onClosed`). A dialog remembers the element focused when it MOUNTS as the one to return
  // to, so mounting it while the dropdown was still closing made it remember a row that is about to go.
  const customPending = useRef(false);
  const [customValue, setCustomValue] = useState('');
  const detected = useMemo(browserTimezone, []);
  const zones = useMemo(supportedTimezones, []);
  const known = new Set(zones);
  const pinned = new Set([detected, 'UTC'].filter(Boolean));
  const items: RowPickerItem[] = [
    { id: '', label: t.pluginCfg.timezoneServerDefault, group: '' },
    ...(detected ? [{ id: detected, label: detected, group: '', status: { icon: LocateFixed, label: t.pluginCfg.timezoneBrowserDetected, tone: 'accent' as const } }] : []),
    ...(detected === 'UTC' ? [] : [{ id: 'UTC', label: 'UTC', group: '' }]),
    ...(value && !pinned.has(value) && !known.has(value) ? [{ id: value, label: value, group: '', status: { icon: Clock, label: t.pluginCfg.timezoneSaved, tone: 'muted' as const } }] : []),
    { id: CUSTOM_TIMEZONE, label: t.pluginCfg.timezoneCustom, group: '' },
    ...zones.filter((zone) => !pinned.has(zone)).map((zone) => ({ id: zone, label: zone, group: 'timezones', groupLabel: t.pluginCfg.timezoneCatalog })),
  ];
  const summary = value || t.pluginCfg.timezoneServerDefault;
  return (
    <>
      <RowPicker
        label={label}
        summary={summary}
        items={items}
        value={value}
        onChange={(next) => {
          // The sentinel is a request for the custom dialog, never a value.
          if (next === CUSTOM_TIMEZONE) {
            setCustomValue(value);
            customPending.current = true;
            return;
          }
          onChange(next || null);
        }}
        onClosed={() => {
          if (!customPending.current) return;
          customPending.current = false;
          setCustomOpen(true);
        }}
      />
      {customOpen ? (
        <Modal title={t.pluginCfg.timezoneCustom} description={hint} onClose={() => setCustomOpen(false)} size="sm">
          <ModalBody>
            <Input
              data-autofocus=""
              value={customValue}
              onChange={(event) => setCustomValue(event.target.value)}
              aria-label={t.pluginCfg.timezoneCustom}
              placeholder={t.pluginCfg.timezoneCustomPlaceholder}
            />
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="ghost" onClick={() => setCustomOpen(false)}>{t.common.cancel}</Button>
            <Button
              type="button"
              variant="accent"
              disabled={!customValue.trim()}
              onClick={() => { onChange(customValue.trim()); setCustomOpen(false); }}
            >
              {t.common.save}
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}
    </>
  );
}

const tokenListValues = (raw: unknown): string[] => Array.isArray(raw)
  ? raw.filter((value): value is string => typeof value === 'string')
  : typeof raw === 'string' && raw !== '' ? [raw] : [];

export function PluginTokenListField({ label, placeholder, value, browse, onChange }: {
  label: string;
  placeholder?: string;
  value: unknown;
  browse?: 'directory';
  onChange: (value: string[]) => void;
}) {
  const [browseOpen, setBrowseOpen] = useState(false);
  const tokens = tokenListValues(value);
  return (
    <>
      <TokenList label={label} value={tokens} onChange={onChange} placeholder={placeholder} mono onBrowse={browse === 'directory' ? () => setBrowseOpen(true) : undefined} />
      {browseOpen ? (
        <DirectoryPicker
          onClose={() => setBrowseOpen(false)}
          onSelect={(path) => {
            const normalized = path.trim();
            if (normalized) onChange(normalizeTokenList([...tokens, normalized]));
            setBrowseOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

/** Shared live-catalog multi-select: the catalog-specific wrappers only map their domain rows into the
 *  same summary card + manage modal the Users drawer uses. Stale stored ids remain selectable. */
function CatalogMultiSelectField({ label, hint, items: catalogItems, value, onChange }: {
  label: string;
  hint?: string;
  items: ManageSelectionItem[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const byId = new Map(catalogItems.map((item) => [item.id, item]));
  const stale = value.filter((id) => !byId.has(id)).map((id) => ({ id, label: id, group: '' }));
  return <ManagedSelectionField label={label} hint={hint} items={[...stale, ...catalogItems]} value={value} onChange={onChange} />;
}

export function ProjectsField({ label, hint, value, onChange }: { label: string; hint?: string; value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const { data: projects = [] } = useProjects();
  return <CatalogMultiSelectField label={label} hint={hint} value={value} onChange={onChange} items={projects.map((project) => ({
    id: String(project.id), label: projectName(project), group: 'projects', groupLabel: t.pluginCfg.catalogProjects,
    icon: <ProjectIcon project={project} size={14} />,
  }))} />;
}

export function PluginsField({ label, hint, value, onChange }: { label: string; hint?: string; value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const { data: plugins = [] } = usePlugins();
  const grantable = plugins.filter((plugin) => plugin.userGrantable && !plugin.removed);
  return <CatalogMultiSelectField label={label} hint={hint} value={value} onChange={onChange} items={grantable.map((plugin) => ({
    id: plugin.name, label: plugin.name, group: 'plugins', groupLabel: t.pluginCfg.catalogPlugins,
    icon: <Puzzle size={14} aria-hidden />,
  }))} />;
}

export function ToolsField({ label, hint, value, onChange }: { label: string; hint?: string; value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const { data: tools = [] } = usePluginTools();
  return <CatalogMultiSelectField label={label} hint={hint} value={value} onChange={onChange} items={tools.map((tool) => ({
    id: tool.name,
    label: tool.label,
    group: tool.plugin ?? tool.group,
    groupLabel: tool.plugin ?? (tool.group === 'memory' ? t.managePicker.toolGroupMemory : t.managePicker.toolGroupElowen),
    icon: <ToolMark name={tool.name} icon={tool.icon} />,
  }))} />;
}

/** Generic `models` config field: the brain catalog as the one model selection (a summary card opening the
 *  manage window). A stored id the catalog no longer offers stays a pinned row, so a save never drops it. */
export function ModelsField({ label, hint, models, value, onChange }: { label: string; hint?: string; models: BrainModelOption[]; value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const offered = new Set(models.map((model) => model.exec));
  const stale = value.filter((exec) => !offered.has(exec)).map((exec) => ({ id: exec, label: exec, group: '', icon: <ModelIcon name={exec} size={14} /> }));
  const { items, groupIcons } = brainModelSelection(models, (model) => model.exec, stale);
  return (
    <ModelSelection
      title={label}
      hint={hint}
      manageAriaLabel={`${t.managePicker.manage}: ${label}`}
      items={items}
      groupIcons={groupIcons}
      value={value}
      onChange={onChange}
      emptyText={t.managePicker.none}
    />
  );
}

/** Provider picker for a `provider`-type field: choose one of the configured brain providers (its
 *  credentials are reused by the plugin, so nothing is entered twice). Filtered to the ones that can
 *  actually authenticate — an API-key entry with its key set, or a CONNECTED OAuth account, which carries
 *  no key at all and was therefore invisible here until the image plugins needed the ChatGPT account —
 *  and, when the field declares `providerType`, to that type (or any of several). Rendering is the shared
 *  dropdown; this wrapper applies the plugin-field filter. */
export function PluginProviderField({ label, value, onChange, providerType }: { label: string; value: string; onChange: (v: string) => void; providerType?: string | string[] }) {
  const { data: config } = useConfig();
  const { data: oauthConnected } = useBrainOauthStatus();
  const { t } = useTranslation();
  const wanted = providerType === undefined ? null : (Array.isArray(providerType) ? providerType : [providerType]);
  // A provider can authenticate when at least one of its ACCOUNTS does: a key of its own, or a login the
  // daemon reports for that connection. One provider carrying a key account and a signed-out login still
  // qualifies through the key.
  const providers = (config?.brain?.providers ?? []).filter((p) =>
    (!wanted || wanted.includes(p.type))
    && p.connections.some((connection) => connection.apiKeySet || connectionLogin(oauthConnected, connection.id)?.connected));
  if (providers.length === 0 && !value) return <p className="text-meta italic text-muted-foreground">{t.pluginCfg.noProviders}</p>;
  const options = providers.map(({ id, label: providerLabel }) => ({ value: id, label: providerLabel }));
  if (value && !providers.some(({ id }) => id === value)) options.unshift({ value, label: value });
  return <ChoiceField title={label} options={options} value={value} onChange={onChange} />;
}
